import { useRef } from 'react'

import { datadogRum } from '@datadog/browser-rum'

import { AlertTriangle, Camera, Crosshair, Scan, Sun } from 'lucide-react'

import type { CdlScanSource } from '../hooks/useDriverCdlAutofill'

/**
 * Full-screen "couldn't read" body for the driver CDL scan (mockup Path 3 —
 * Couldn't read → retake). Rendered by LicenseScreen as a takeover when the
 * scan resolves to 'rejected': retake is the first-offered recovery, uploading
 * a different photo is second, and manual entry stays reachable as the escape
 * hatch. Owns its own hidden camera / picker inputs so the driver goes straight
 * to the OS camera or file picker without re-opening the source sheet.
 */
interface CdlRetakeContentProps {
  // `source` is the entry the driver used (RUM route dimension, ENG-4304):
  // the camera button reports 'take_photo', the upload button 'photo_library'.
  onFileSelected: (file: File, source: CdlScanSource) => void
  // Leaves the scan flow and returns the driver to the manual form.
  onManualEntry: () => void
  // RUM returning_applicant dimension (ENG-4362) for the scan_opened emits.
  returningApplicant: boolean
}

const CdlRetakeContent = ({
  onFileSelected,
  onManualEntry,
  returningApplicant,
}: CdlRetakeContentProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  // Which button opened the current input — both inputs share one onChange.
  const sourceRef = useRef<CdlScanSource>('photo_library')

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset the input so re-picking the same file still fires onChange.
    e.target.value = ''
    if (file) onFileSelected(file, sourceRef.current)
  }

  return (
    <div className="col-span-3 flex w-full flex-col">
      <span className="mb-3 mt-1 flex h-11 w-11 items-center justify-center rounded-full bg-doubleNickel-warning-50 text-doubleNickel-warning-600">
        <AlertTriangle size={22} strokeWidth={2} />
      </span>
      <div className="mb-1.5 text-lg font-bold tracking-tight text-doubleNickel-gray-900">
        Let&apos;s try that photo again
      </div>
      <p className="mb-3.5 text-[12.5px] leading-normal text-doubleNickel-gray-600">
        We couldn&apos;t read the front of your CDL — it looks blurry or has
        glare. A quick retake usually fixes it.
      </p>
      <div className="mb-4 flex items-center gap-3 rounded-lg border border-doubleNickel-warning-200 bg-doubleNickel-warning-50 p-2.5">
        <span className="h-[38px] w-[54px] flex-none rounded-md border border-doubleNickel-warning-200 bg-doubleNickel-gray-100" />
        <span className="text-xs font-semibold text-doubleNickel-warning-700">
          CDL — front · couldn&apos;t read
        </span>
      </div>
      <ul className="mb-[18px] flex flex-col gap-2.5">
        <li className="flex items-start gap-2.5 text-[12.5px] leading-snug text-doubleNickel-gray-700">
          <Scan
            size={15}
            className="mt-0.5 flex-none text-doubleNickel-gray-400"
          />
          Fit the whole card in view
        </li>
        <li className="flex items-start gap-2.5 text-[12.5px] leading-snug text-doubleNickel-gray-700">
          <Sun
            size={15}
            className="mt-0.5 flex-none text-doubleNickel-gray-400"
          />
          Avoid glare and shadows
        </li>
        <li className="flex items-start gap-2.5 text-[12.5px] leading-snug text-doubleNickel-gray-700">
          <Crosshair
            size={15}
            className="mt-0.5 flex-none text-doubleNickel-gray-400"
          />
          Hold steady so the text is sharp
        </li>
      </ul>
      <div className="flex flex-col gap-2.5">
        <button
          type="button"
          onClick={() => {
            datadogRum.addAction('driver_cdl_scan_opened', {
              component: 'CdlRetakeContent',
              route: 'take_photo',
              returning_applicant: returningApplicant,
            })
            sourceRef.current = 'take_photo'
            cameraInputRef.current?.click()
          }}
          className="flex items-center justify-center gap-2 rounded bg-doubleNickel-brand-500 p-3 text-sm font-semibold text-white"
        >
          <Camera size={15} strokeWidth={2} />
          Retake photo
        </button>
        <button
          type="button"
          onClick={() => {
            datadogRum.addAction('driver_cdl_scan_opened', {
              component: 'CdlRetakeContent',
              route: 'photo_library',
              returning_applicant: returningApplicant,
            })
            sourceRef.current = 'photo_library'
            fileInputRef.current?.click()
          }}
          className="rounded border border-doubleNickel-brand-200 bg-white p-3 text-sm font-semibold text-doubleNickel-brand-600"
        >
          Upload a different photo
        </button>
      </div>
      <button
        type="button"
        onClick={onManualEntry}
        className="mt-1.5 text-center text-[12.5px] text-doubleNickel-gray-500 underline"
      >
        Enter the details manually instead
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={handleFileChange}
      />
      {/* capture="environment" opens the OS rear camera directly — its native
          preview supplies the mockup's retake/use-photo screens. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  )
}

export default CdlRetakeContent
